// src/pages/supplier/QualityInspectionPage.jsx
// Phase 5 — M5: Supplier quality inspection management

import { useEffect, useState } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const STATUS_STYLES = {
  pending:     { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  assigned:    { bg: '#DBEAFE', color: '#1E40AF', label: 'Assigned' },
  in_progress: { bg: '#EDE9FE', color: '#5B21B6', label: 'In Progress' },
  completed:   { bg: '#DCFCE7', color: '#166534', label: 'Completed' },
  cancelled:   { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelled' },
}

const VERDICT_STYLES = {
  pass:        { bg: '#DCFCE7', color: '#166534', icon: '✅', label: 'PASS' },
  fail:        { bg: '#FEE2E2', color: '#991B1B', icon: '❌', label: 'FAIL' },
  conditional: { bg: '#FEF3C7', color: '#92400E', icon: '⚠️', label: 'CONDITIONAL' },
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] ?? { bg: GR_LT, color: GREY, label: status }
  return <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{s.label}</span>
}

function VerdictBadge({ verdict }) {
  if (!verdict) return null
  const v = VERDICT_STYLES[verdict] ?? { bg: GR_LT, color: GREY, icon: '?', label: verdict }
  return <span style={{ background: v.bg, color: v.color, padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800 }}>{v.icon} {v.label}</span>
}

function StatCard({ label, value, color = PINK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '0.88rem', marginTop: 2 }}>{label}</div>
    </div>
  )
}

export default function QualityInspectionPage() {
  const [summary, setSummary] = useState(null)
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [expanded, setExpanded] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ product_id: '', batch_quantity: '', type: 'pre_dispatch', order_id: '', batch_reference: '', buyer_requirements: '' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      api('/supplier/quality/summary'),
      api('/supplier/quality'),
    ]).then(([sRes, rRes]) => {
      setSummary(sRes.data ?? sRes)
      setRequests((rRes.data?.data ?? rRes.data ?? []))
      setLoading(false)
    })
  }

  useEffect(() => { load() }, [])

  const submit = async () => {
    setSaving(true)
    await api('/supplier/quality', { method: 'POST', body: JSON.stringify(form) })
    setSaving(false); setShowModal(false)
    setForm({ product_id: '', batch_quantity: '', type: 'pre_dispatch', order_id: '', batch_reference: '', buyer_requirements: '' })
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading quality inspections…</div>

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Quality Inspection</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Request and track pre-dispatch quality checks for your batches</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem' }}>
          + Request Inspection
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard label="Total Requests" value={summary?.total    ?? 0} color={DARK} />
        <StatCard label="Pending"        value={summary?.pending  ?? 0} color={ORG}  />
        <StatCard label="Passed"         value={summary?.passed   ?? 0} color="#10B981" />
        <StatCard label="Failed"         value={summary?.failed   ?? 0} color="#EF4444" />
      </div>

      {/* Requests Table */}
      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, color: DARK, fontSize: '0.95rem' }}>
          Inspection Requests
        </div>
        {requests.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: GREY }}>No inspection requests yet. Submit your first request to start quality tracking.</div>
        ) : (
          <div>
            {requests.map(req => {
              const report  = req.report
              const isOpen  = expanded === req.id
              return (
                <div key={req.id}>
                  <div
                    onClick={() => setExpanded(isOpen ? null : req.id)}
                    style={{ padding: '16px 24px', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 120px', alignItems: 'center', gap: 12, background: isOpen ? '#FAFAFA' : '#fff' }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>{req.request_number}</div>
                      <div style={{ color: GREY, fontSize: '0.78rem', marginTop: 2 }}>{req.product?.name ?? `Product #${req.product_id}`}</div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#4B5563' }}>{req.type?.replace('_',' ')}</div>
                    <div style={{ fontSize: '0.85rem', color: '#4B5563' }}>{req.batch_quantity} m</div>
                    <div><StatusBadge status={req.status} /></div>
                    <div>{report && <VerdictBadge verdict={report.verdict} />}</div>
                    <div style={{ textAlign: 'right', color: GREY, fontSize: '0.8rem' }}>
                      {new Date(req.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                      <span style={{ marginLeft: 8, color: PINK }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ padding: '20px 24px 24px', background: '#FAFAFA', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        {/* Request details */}
                        <div>
                          <div style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.88rem' }}>Request Details</div>
                          {[['Batch Ref', req.batch_reference || '—'], ['Order ID', req.order_id || '—'], ['Inspection Date', req.inspection_date || 'TBD'], ['Assigned At', req.assigned_at ? new Date(req.assigned_at).toLocaleDateString('en-IN') : '—']].map(([l,v]) => (
                            <div key={l} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.84rem' }}>
                              <span style={{ color: GREY, minWidth: 120 }}>{l}:</span>
                              <span style={{ color: DARK, fontWeight: 600 }}>{v}</span>
                            </div>
                          ))}
                          {req.buyer_requirements && (
                            <div style={{ marginTop: 8, padding: '10px 12px', background: '#EFF6FF', borderRadius: 8, fontSize: '0.82rem', color: '#1E40AF' }}>
                              <strong>Requirements:</strong> {req.buyer_requirements}
                            </div>
                          )}
                        </div>
                        {/* QC Report */}
                        <div>
                          {report ? (
                            <div>
                              <div style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.88rem' }}>QC Report</div>
                              <div style={{ marginBottom: 8 }}><VerdictBadge verdict={report.verdict} /></div>
                              {[['Defect Rate', `${report.defect_rate_pct}%`], ['Pass Rate', `${report.pass_pct}%`], ['AQL Level', report.aql_level || '—']].map(([l,v]) => (
                                <div key={l} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.84rem' }}>
                                  <span style={{ color: GREY, minWidth: 100 }}>{l}:</span>
                                  <span style={{ color: DARK, fontWeight: 600 }}>{v}</span>
                                </div>
                              ))}
                              <div style={{ marginTop: 8, padding: '10px 12px', background: report.verdict === 'pass' ? '#F0FDF4' : '#FFF5F5', borderRadius: 8, fontSize: '0.82rem', color: report.verdict === 'pass' ? '#166534' : '#991B1B' }}>
                                {report.summary}
                              </div>
                              {report.corrective_action && (
                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#FFFBEB', borderRadius: 8, fontSize: '0.8rem', color: '#92400E' }}>
                                  <strong>Action Required:</strong> {report.corrective_action}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: GREY, fontSize: '0.87rem', textAlign: 'center', padding: 24 }}>
                              Report not yet submitted.<br/>Inspector will update once complete.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Request Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Request Quality Inspection</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Product ID *</label>
                <input type="number" value={form.product_id} onChange={e => setForm(f => ({...f,product_id:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Batch Quantity (metres) *</label>
                <input type="number" value={form.batch_quantity} onChange={e => setForm(f => ({...f,batch_quantity:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Inspection Type</label>
                <select value={form.type} onChange={e => setForm(f => ({...f,type:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                  {[['pre_dispatch','Pre-Dispatch'],['incoming','Incoming'],['periodic','Periodic']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Batch / Lot Reference</label>
                <input value={form.batch_reference} onChange={e => setForm(f => ({...f,batch_reference:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Buyer Requirements / Notes</label>
                <textarea rows={3} value={form.buyer_requirements} onChange={e => setForm(f => ({...f,buyer_requirements:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submit} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
